/**
 * src/components/shared/OfflineModeButton.jsx — top-right "offline mode" control.
 *
 * CEO request 2026-06-12: an airplane-style button that (1) programmatically
 * warms every tab's cache in depth so the CEO doesn't open them one-by-one
 * before a flight, and (2) toggles a forced "offline preview" to verify the
 * cached tabs render without enabling real airplane mode.
 *
 * Data only — images/heavy media stay online; subtab JS is already precached by
 * the service worker. See lib/offline/prepareOffline.js + networkStatus.js.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Plane, Download, Check, X, AlertTriangle, Loader } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';
import { prepareOfflineCache, getLastPreparedAt, OFFLINE_DOMAINS } from '../../lib/offline/prepareOffline';
import { isForcedOffline, setForcedOffline, getOnline, subscribe } from '../../lib/offline/networkStatus';

function useRelativeTime(t, ts) {
  if (!ts) return t('offline.neverPrepared');
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 1) return t('offline.lastUpdated', { time: t('offline.justNow') });
  if (mins < 60) return t('offline.lastUpdated', { time: t('offline.minutesAgo', { count: mins }) });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('offline.lastUpdated', { time: t('offline.hoursAgo', { count: hrs }) });
  return t('offline.lastUpdated', { time: t('offline.daysAgo', { count: Math.floor(hrs / 24) }) });
}

export default function OfflineModeButton({ align = 'right' } = {}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [forced, setForced] = useState(isForcedOffline());
  const [online, setOnline] = useState(getOnline());
  const [preparing, setPreparing] = useState(false);
  const [progress, setProgress] = useState({}); // key -> { status, count }
  const [preparedAt, setPreparedAt] = useState(getLastPreparedAt());
  const panelRef = useRef(null);

  // Reflect real connectivity changes in the icon.
  useEffect(() => subscribe((v) => setOnline(v)), []);

  // Close the panel on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleDownload = useCallback(async () => {
    if (preparing) return;
    setPreparing(true);
    setProgress({});
    const out = await prepareOfflineCache((evt) => {
      setProgress((prev) => ({ ...prev, [evt.key]: { status: evt.status, count: evt.count } }));
    });
    setPreparing(false);
    if (!out.offline) setPreparedAt(getLastPreparedAt());
  }, [preparing]);

  const handleTogglePreview = useCallback(() => {
    const next = !forced;
    setForcedOffline(next);
    setForced(next);
    // Reload so the app cold-boots in the new mode and hydrates EXACTLY from
    // cache (the faithful "what you'd see on a plane" view) — the same path as
    // the iOS reopen we're reproducing. forcedOffline is persisted, so it holds
    // across the reload. Guarded for jsdom (reload throws there).
    try { window.location.reload(); } catch { setOnline(getOnline()); }
  }, [forced]);

  const active = forced || !online;
  const relTime = useRelativeTime(t, preparedAt);

  return (
    <div ref={panelRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        data-testid="offline-mode-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('offline.ariaButton')}
        aria-expanded={open}
        style={{
          width: 44, height: 44, minHeight: 44, borderRadius: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: active ? 'var(--navy-surface)' : 'var(--accent-bg)',
          color: active ? 'var(--inv)' : 'var(--accent)',
          // Inset hairline so the inactive --glass state stays visible on a
          // light/cream surface (TICKET-205); harmless under the active navy.
          border: 'none', boxShadow: active ? 'none' : 'inset 0 0 0 1px var(--bdr)',
          cursor: 'pointer', position: 'relative',
          transition: 'background 150ms var(--ease)',
        }}
      >
        <Plane size={20} strokeWidth={1.75} />
        {forced && (
          <span style={{
            position: 'absolute', top: 6, right: 6, width: 8, height: 8,
            borderRadius: '50%', background: 'var(--warn)',
          }} />
        )}
      </button>

      {open && (
        <div
          data-testid="offline-mode-panel"
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', zIndex: 1200,
            // align='left' opens the panel INTO content (used in the narrow
            // tablet rail where right:0 would push it off the left edge).
            ...(align === 'left' ? { left: 0 } : { right: 0 }),
            width: 290, maxWidth: 'calc(100vw - 24px)',
            background: 'var(--card)', border: '1px solid var(--bdr)', borderRadius: 14,
            boxShadow: '0 12px 32px rgba(0,0,0,0.22)', padding: 14,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--t1)' }}>{t('offline.title')}</div>
              <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 2, lineHeight: 1.35 }}>{t('offline.subtitle')}</div>
            </div>
            <button
              data-testid="offline-mode-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{ width: 44, height: 44, minHeight: 44, marginTop: -10, marginRight: -8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', color: 'var(--tm)', cursor: 'pointer' }}
            >
              <X size={18} strokeWidth={1.5} />
            </button>
          </div>

          {/* Download / warm-all */}
          <button
            data-testid="offline-mode-download"
            onClick={handleDownload}
            disabled={preparing || !online}
            style={{
              marginTop: 12, width: '100%', minHeight: 44, padding: '10px 14px', borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              background: (preparing || !online) ? 'var(--glass)' : 'var(--navy-surface)',
              color: (preparing || !online) ? 'var(--tm)' : 'var(--inv)',
              border: 'none', cursor: (preparing || !online) ? 'default' : 'pointer',
              fontSize: 13, fontWeight: 600,
              opacity: (!online && !preparing) ? 0.6 : 1,
            }}
          >
            {preparing
              ? <><Loader size={16} style={{ animation: 'spin 800ms linear infinite' }} /> {t('offline.downloading')}</>
              : <><Download size={16} strokeWidth={2} /> {preparedAt ? t('offline.refresh') : t('offline.download')}</>}
          </button>

          {!online && !preparing && (
            <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 8, textAlign: 'center' }}>{t('offline.needOnline')}</div>
          )}

          {/* Per-tab checklist */}
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {OFFLINE_DOMAINS.map((d) => {
              const p = progress[d.key];
              const status = p?.status;
              return (
                <div key={d.key} data-testid={`offline-row-${d.key}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 4px', fontSize: 12, color: 'var(--t1)' }}>
                  <span>{d.label}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--tm)', fontSize: 11 }}>
                    {status === 'done' && <><span>{t('offline.rows', { count: p.count ?? 0 })}</span><Check size={14} color="var(--ok)" /></>}
                    {status === 'running' && <Loader size={13} style={{ animation: 'spin 800ms linear infinite' }} />}
                    {status === 'error' && <AlertTriangle size={13} color="var(--bad)" />}
                  </span>
                </div>
              );
            })}
          </div>

          <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 8 }}>{relTime}</div>

          {/* Preview toggle */}
          <div style={{ borderTop: '1px solid var(--bdr)', marginTop: 12, paddingTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>{t('offline.previewTitle')}</div>
                <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 2, lineHeight: 1.35 }}>{t('offline.previewHint')}</div>
              </div>
              <button
                data-testid="offline-mode-preview-toggle"
                onClick={handleTogglePreview}
                role="switch"
                aria-checked={forced}
                style={{
                  width: 48, height: 28, minHeight: 28, borderRadius: 999, border: 'none', cursor: 'pointer',
                  background: forced ? 'var(--navy)' : 'var(--bdr)',
                  position: 'relative', flexShrink: 0, transition: 'background 150ms var(--ease)',
                }}
              >
                <span style={{
                  position: 'absolute', top: 3, left: forced ? 23 : 3, width: 22, height: 22,
                  borderRadius: '50%', background: 'var(--card)', transition: 'left 150ms var(--ease)',
                }} />
              </button>
            </div>
            {forced && (
              <div data-testid="offline-mode-preview-on" style={{ fontSize: 11, color: 'var(--warn)', marginTop: 8, fontWeight: 600 }}>{t('offline.previewOn')}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
